import styled from "styled-components";
import { LocationRow } from "./LocationRow";
import { LocationGroupTitle } from "./LocationGroupTitle";
import { sumOver } from "../../../helpers/mapHelpers";
import trackerHelper from "../../../helpers/tracker";
import { FixaSkeleton } from "@coworker/apprestructured/src/shared/wrappers/FixaSkeleton/FixaSkeleton";

const LocationsList = styled("div")`
  overflow-y: auto;
  overflow-x: hidden;
`;

const LocationSkeleton = styled("div")`
  padding: 15px 24px 16px 24px;
  border-bottom: 1px solid var(--grey200);
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

type LocationObject = {
  locationCode: string;
  locationSection: string;
  locationDepartment: string;
  locationQuantity: number;
  relatedOnThisLocation: number;
  locationCustom?: string;
};

interface LocationGroupProps {
  isCustomLocation: boolean;
  locationGroupTitle: string;
  locationsList: any;
  relatedAddons: any;
  selectedLocationCode: string;
  submitWithConfirmation: (locationObject: LocationObject) => void;
  isProductQualityTask: boolean | undefined;
  isPickupLocationAndSFM: boolean;
  isRefillLocation?: boolean;
  articleNo: string;
  isLoadingCustomLocations?: boolean;
}

export function LocationGroup({
  isCustomLocation,
  locationGroupTitle,
  locationsList,
  relatedAddons,
  selectedLocationCode,
  submitWithConfirmation,
  isProductQualityTask,
  isPickupLocationAndSFM,
  isRefillLocation,
  articleNo,
  isLoadingCustomLocations = false,
}: LocationGroupProps) {
  const getRelatedOnThisLocation = (code: string) => {
    return sumOver(
      relatedAddons[code],
      (task: any) => task?.requested_quantity
    );
  };

  const showUnit = !(isCustomLocation || isProductQualityTask);

  const renderSkeletonItems = () => {
    const skeletonCount = locationsList?.length > 0 ? locationsList.length : 1;

    return Array.from({ length: skeletonCount }, (_, index) => (
      <LocationSkeleton
        key={`skeleton-${index}`}
        data-testid="location-skeleton"
      >
        <FixaSkeleton height="16px" width="60%" />
        <FixaSkeleton height="16px" width="60%" />
      </LocationSkeleton>
    ));
  };

  const shouldShowGroup = isLoadingCustomLocations || !!locationsList.length;

  if (!shouldShowGroup) {
    return null;
  }

  return (
    <div>
      <LocationGroupTitle
        groupTitleTranslationKey={locationGroupTitle}
        showUnit={showUnit}
      />
      {isLoadingCustomLocations ? (
        <LocationsList>{renderSkeletonItems()}</LocationsList>
      ) : (
        <LocationsList>
          {locationsList.map((location: any) => {
            const relatedOnThisLocation = getRelatedOnThisLocation(
              location.code
            );
            return (
              <LocationRow
                isPickupLocationAndSFM={isPickupLocationAndSFM}
                isProductQualityTask={isProductQualityTask}
                key={location.code}
                selectedLocation={selectedLocationCode}
                onClick={() => {
                  submitWithConfirmation({
                    locationCode: location.code,
                    locationSection: isProductQualityTask
                      ? location.code
                      : location.section,
                    locationDepartment: location.department,
                    locationQuantity: location.quantity,
                    relatedOnThisLocation,
                  });
                  trackerHelper.trackCutomLocationItemClick(location.code);
                }}
                relatedOnThisLocation={relatedOnThisLocation}
                {...location}
                isCustomLocation={isCustomLocation}
                articleNo={articleNo}
                isRefillLocation={isRefillLocation}
              />
            );
          })}
        </LocationsList>
      )}
    </div>
  );
}
